import { Base } from '@studiometa/js-toolkit';

/**
 * CampaignPushes
 *
 * N'affiche qu'un push de campagne à la fois : le plus récemment démarré parmi ceux
 * dont la portée correspond à la contenance sélectionnée.
 *
 * Liquid rend une carte par campagne en cours éligible à la fiche, toutes masquées,
 * chacune portant sa priorité (`data-priority`, date de début en secondes) et les
 * contenances qui la rendent candidate (`data-variants`, vide = aucune condition de
 * contenance). Départager en Liquid aurait imposé deux passes sur les campagnes ; le
 * faire ici permet surtout de rebasculer sur une campagne plus large quand la
 * prioritaire ne couvre pas la contenance choisie.
 *
 * L'écoute est déléguée au document plutôt que posée sur l'input : `ProductInfo`
 * réinjecte du HTML à chaque `variantChange`, ce qui remplace le nœud de l'input et
 * détacherait un listener direct dès le premier changement de contenance.
 */
export default class CampaignPushes extends Base {
  /**
   * Component Configuration
   */
  static config = {
    name: 'CampaignPushes',
    refs: ['push[]'],
    options: {
      form: {
        type: String,
        default: () => '#product-form',
      },
    },
  };

  /**
   * mounted
   * @returns {void}
   */
  mounted() {
    this.onDocumentChange = this.handleChange.bind(this);
    document.addEventListener('change', this.onDocumentChange);
    this.update();
  }

  /**
   * destroyed
   * @returns {void}
   */
  destroyed() {
    document.removeEventListener('change', this.onDocumentChange);
  }

  /**
   * Ne retient que les changements de l'input de variante du formulaire produit.
   * @param {Event} event
   * @returns {void}
   */
  handleChange(event) {
    const input = event.target;

    if (!input || input.name !== 'id') return;
    if (!input.closest(this.$options.form)) return;

    this.update();
  }

  /**
   * Contenance actuellement sélectionnée.
   * @returns {number|null}
   */
  get selectedVariantId() {
    const input = document.querySelector(`${this.$options.form} input[name="id"]`);

    return input ? Number.parseInt(input.value, 10) : null;
  }

  /**
   * Révèle la carte gagnante et masque les autres.
   * @returns {void}
   */
  update() {
    const selectedId = this.selectedVariantId;
    let winner = null;
    let winnerPriority = -1;

    for (const push of this.$refs.push) {
      const priority = Number.parseInt(push.dataset.priority, 10) || 0;
      let variants = [];

      try {
        variants = JSON.parse(push.dataset.variants) ?? [];
      } catch {
        variants = [];
      }

      // Liste vide : portée produit, la carte est candidate sans condition.
      const matches = variants.length <= 0 || variants.includes(selectedId);

      if (matches && priority >= winnerPriority) {
        winner = push;
        winnerPriority = priority;
      }
    }

    for (const push of this.$refs.push) {
      push.classList.toggle('hidden', push !== winner);
      push.classList.toggle('flex', push === winner);
    }
  }
}
